import React from 'react';
import { salonInfo } from '../data/mock';
import { useLanguage } from '../i18n/LanguageContext';

export const CinderellaProgram = () => {
  const { t } = useLanguage();
  const openWhatsApp = () => {
    window.open(`https://wa.me/${salonInfo.whatsapp.replace(/\s|\+/g, '')}`, '_blank');
  };

  const c = t.cinderella;

  return (
    <div
      data-testid="cinderella-section"
      className="bg-neutral-950 text-neutral-100 relative overflow-hidden py-20 md:py-28 mt-8"
    >
      <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-[#c8b6d9] opacity-[0.05] blur-[140px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-[#9bab9a] opacity-[0.03] blur-[140px] pointer-events-none"></div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 relative">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="flex items-center gap-4 mb-8">
            <span className="divider-line"></span>
            <span className="eyebrow text-lilac">{c.eyebrow}</span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-neutral-100 tracking-tight mb-6">
            {c.titleA}
            <span className="block italic text-lilac font-light">{c.titleB}</span>
          </h2>
          <p className="eyebrow text-sage mb-8">{c.developedBy}</p>
          <div className="space-y-5 max-w-2xl">
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed font-light">
              {c.p1}
            </p>
            <p className="text-base md:text-lg text-neutral-500 leading-relaxed font-light">
              {c.p2}
            </p>
          </div>
        </div>

        {/* HERO Pricing Box */}
        <div
          data-testid="cinderella-pricing-hero"
          className="relative border border-lilac/30 bg-gradient-to-br from-lilac/[0.08] via-white/[0.02] to-transparent p-8 md:p-14 mb-16 overflow-hidden"
        >
          {/* Desktop-only absolute badge */}
          <div className="hidden md:block absolute md:top-6 md:right-6">
            <span className="eyebrow text-lilac border border-lilac/40 px-3 py-1.5">
              {c.oneTime}
            </span>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-14 md:items-end">
            <div className="md:col-span-7">
              {/* Mobile-only inline badge (top of stack) */}
              <div className="md:hidden mb-8">
                <span
                  data-testid="cinderella-onetime-badge-mobile"
                  className="eyebrow text-lilac border border-lilac/40 px-3 py-1.5 inline-block"
                >
                  {c.oneTime}
                </span>
              </div>

              <p className="eyebrow text-neutral-400 mb-4">{c.originalLabel}</p>
              <p className="font-display text-3xl md:text-4xl text-neutral-500 line-through font-light tracking-tight mb-6 md:mb-3">
                {c.originalValue}
              </p>
              <p className="eyebrow text-sage mb-10 md:mb-8">
                {c.savingLabel} <span className="text-sage">{c.savingAmount}</span>
              </p>

              <p className="eyebrow text-lilac mb-4 md:mb-3">{c.introLabel}</p>
              <div className="flex items-baseline gap-4 md:gap-5 mb-6 md:mb-5">
                <span className="font-display text-7xl md:text-8xl lg:text-9xl text-lilac tracking-tight leading-none">
                  {c.introPrice}
                </span>
              </div>
              <p className="font-display italic text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-neutral-100 font-light leading-snug tracking-tight">
                {c.perTreatment}
              </p>
            </div>

            <div className="md:col-span-5 space-y-8 md:space-y-6 mt-10 md:mt-0">
              <div className="border-l-2 border-lilac/50 pl-5">
                <p className="eyebrow text-neutral-400 mb-3 md:mb-2">{c.rhythmLabel}</p>
                <p className="font-display text-xl sm:text-2xl md:text-3xl text-neutral-100 leading-snug">
                  {c.rhythmInterval}
                </p>
              </div>

              <button
                onClick={openWhatsApp}
                data-testid="cinderella-cta-whatsapp"
                className="group w-full inline-flex items-center justify-between gap-4 bg-lilac hover:bg-white text-neutral-950 px-8 py-6 eyebrow transition-all duration-500"
              >
                {c.ctaBook}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>

              <p className="text-sm text-neutral-500 font-light leading-relaxed">
                {c.combinable}
              </p>
            </div>
          </div>
        </div>

        {/* 4 treatment steps with SHORT descriptions */}
        <div className="grid md:grid-cols-2 gap-px bg-white/5 mb-16">
          {c.steps.map((step, i) => {
            const n = String(i + 1).padStart(2, '0');
            return (
              <div
                key={n}
                data-testid={`cinderella-step-${n}`}
                className="bg-[#0e0e11] p-10 md:p-12 hover:bg-[#131319] transition-colors duration-500"
              >
                <p className="font-display text-sm text-lilac tabular-nums mb-8">{n}</p>
                <h3 className="font-display text-2xl md:text-3xl text-neutral-100 mb-2">
                  {step.title}
                </h3>
                <p className="eyebrow text-sage mb-6">{step.sub}</p>
                <div className="text-neutral-400 leading-relaxed font-light space-y-3">
                  {step.desc.split('\n\n').map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Flexible entry — Calming Care credit option */}
        <div
          data-testid="cinderella-credit-option"
          className="border-l-2 border-sage/50 bg-white/[0.02] px-7 md:px-10 py-7 md:py-9 mb-16"
        >
          <p className="eyebrow text-sage mb-4">{c.creditOptionLabel}</p>
          <p className="text-base md:text-lg text-neutral-300 leading-relaxed font-light max-w-3xl">
            {c.creditOption}
          </p>
        </div>

        {/* 3. Folgebuchung — single block, just once */}
        <div
          data-testid="cinderella-followup"
          className="border-t border-white/10 pt-14"
        >
          <p className="eyebrow text-lilac mb-6">{c.followUpLabel}</p>
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
            <div className="md:col-span-6">
              <div className="flex items-baseline gap-4 mb-3">
                <span className="font-display text-6xl md:text-7xl text-neutral-100 tracking-tight leading-none">
                  {c.followUpPrice}
                </span>
              </div>
              <p className="font-display italic text-xl md:text-2xl text-neutral-300 font-light leading-snug">
                {c.followUpPerTreatment}
              </p>
            </div>
            <div className="md:col-span-6">
              <p className="text-base md:text-lg text-neutral-400 font-light leading-relaxed">
                {c.followUpNote}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
